/* ============================================================
   HYDRO PLANT — Product sticky nav (v2, Cowboy-style bar)

   Four jobs:
     1. Reveal / hide the fixed bar, and either slide the theme
        header out of its way or pin underneath it.
     2. Smooth-scroll to each link's target and highlight the
        section currently in view.
     3. Drive the variant picker for the Add to cart and Dynamic
        payment actions.
     4. Forward the optional mobile menu button to the theme's
        own site menu drawer.

   THE MENU OPENER IS .m-hamburger-box, NOT .m-menu-button
   mega-menu.js binds the drawer toggle to .m-hamburger-box.
   .m-menu-button is only the wrapping <span> and has no listener
   of its own, so clicking it does nothing at all — which looked
   exactly like "the bar hides but no menu appears". The click
   toggles, so the same call closes an open drawer. Verified at
   desktop width, so the handler is not viewport-gated.

   THE DRAWER LIVES INSIDE THE HEADER
   #m-menu-drawer is a descendant of .m-header__wrapper — the
   very element Replace mode translates off-screen and sets
   pointer-events: none on. Three things follow:
     - the drawer is position: absolute, so a transform on that
       wrapper drags it off-screen with the header
     - pointer-events: none makes every link inside it dead
     - our bar at z-index 84 paints over the drawer at 59
   So opening the drawer RELEASES the header push first and gets
   our bar out of the way, then puts both back when the drawer
   closes. That is what menuOpen and .is-menu-open are for.
   Do not "simplify" pushHeader to ignore menuOpen.

   NO DEAD CONTROLS
   A menu button with no theme hamburger behind it is removed. A
   nav link whose target does not resolve is hidden on the
   storefront, but left visible and struck through in the theme
   editor so the merchant sees the typo.

   A same-src <script> runs once per tag, so everything below is
   wrapped in a one-time guard and re-entered through init().
   ============================================================ */
(function () {
  'use strict';

  if (window.__hpPnavLoaded) return;
  window.__hpPnavLoaded = true;

  var HEADER_SELECTORS = ['.m-header__wrapper', 'm-header', '#shopify-section-header', 'header'];
  /* Ordered by what actually carries the listener. */
  var MENU_OPENERS = ['.m-hamburger-box', '.m-menu-button'];
  var MENU_DRAWER = '#m-menu-drawer';
  var MENU_OPEN_CLASS = 'open';
  var SPY_PAD = 24;

  function inEditor() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function firstMatch(list) {
    for (var i = 0; i < list.length; i++) {
      var el = document.querySelector(list[i]);
      if (el) return el;
    }
    return null;
  }

  function raf(fn) {
    var pending = false;
    return function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        fn();
      });
    };
  }

  function resolveTarget(sel) {
    if (!sel) return null;
    var s = sel.trim();
    try {
      var el = document.querySelector(s);
      if (el) return el;
    } catch (e) {
      /* not a valid selector — fall through and try it as a bare id */
    }
    if (s.charAt(0) !== '#' && s.charAt(0) !== '.') {
      return document.getElementById(s);
    }
    return null;
  }

  /* ==========================================================
     BAR
     ========================================================== */

  function Bar(root) {
    this.root = root;
    this.bar = root.querySelector('[data-pnav-bar]');
    this.sentinel = root.querySelector('[data-pnav-sentinel]');
    if (!this.bar) return;

    this.headerMode = root.dataset.headerMode || 'replace';
    this.revealMode = root.dataset.reveal || 'scroll';
    this.offset = parseInt(root.dataset.offset, 10) || 0;
    this.hideAfterLast = root.dataset.hideAfterLast === 'true';
    this.reserve = root.dataset.reserve === 'true';

    this.header = firstMatch(HEADER_SELECTORS);
    this.headerPushed = false;
    this.menuOpen = false;
    this.on = false;

    this.links = Array.prototype.slice.call(root.querySelectorAll('[data-pnav-link]'));
    this.spots = [];
    this.orphans = [];
    this.mapTargets();

    this.bindLinks();
    this.bindToggle();
    this.bindMenu();

    this.tick = raf(this.update.bind(this));
    window.addEventListener('scroll', this.tick, { passive: true });
    window.addEventListener('resize', this.tick);

    if (this.reserve) this.root.style.minHeight = this.barHeight() + 'px';

    this.update();
  }

  Bar.prototype.barHeight = function () {
    return this.bar.offsetHeight || 56;
  };

  /* One entry per distinct target, carrying every link that points at it,
     so duplicated desktop / mobile links highlight together. Links whose
     target is not on the page are collected separately.

     Note .hidden cannot be used to hide them: the UA sheet's
     [hidden] { display: none } loses to our own display: inline-flex on
     .hp-pnav .hp-pnav__link, so it has to be an inline display. */
  Bar.prototype.mapTargets = function () {
    var byTarget = [];
    var orphans = [];

    this.links.forEach(function (a) {
      var el = resolveTarget(a.getAttribute('data-target'));
      if (!el) { orphans.push(a); return; }
      var found = null;
      for (var i = 0; i < byTarget.length; i++) {
        if (byTarget[i].el === el) found = byTarget[i];
      }
      if (found) found.links.push(a);
      else byTarget.push({ el: el, links: [a] });
    });

    var editing = inEditor();
    orphans.forEach(function (a) {
      if (editing) {
        a.style.opacity = '0.4';
        a.style.textDecoration = 'line-through';
        a.title = 'Nothing on this page matches ' + (a.getAttribute('data-target') || '');
      } else {
        a.style.display = 'none';
      }
    });

    this.spots = byTarget;
    this.orphans = orphans;
  };

  Bar.prototype.headerBottom = function () {
    if (!this.header) return 0;
    var cs = getComputedStyle(this.header);
    if (cs.position !== 'fixed' && cs.position !== 'sticky') return 0;
    return Math.max(0, this.header.getBoundingClientRect().bottom);
  };

  Bar.prototype.topFor = function () {
    if (this.headerMode === 'below') return this.headerBottom() + this.offset;
    return this.offset;
  };

  /* Idempotent, and refuses to push while the menu drawer is open, because
     the drawer is inside the element being pushed. */
  Bar.prototype.pushHeader = function (push) {
    if (this.headerMode !== 'replace' || !this.header) return;
    if (this.menuOpen) push = false;
    if (push === this.headerPushed) return;
    this.headerPushed = push;
    var st = this.header.style;
    if (push) {
      st.transition = 'transform .34s cubic-bezier(.4,0,.2,1)';
      st.transform = 'translate3d(0,-101%,0)';
      st.pointerEvents = 'none';
    } else {
      st.transform = '';
      st.pointerEvents = '';
    }
  };

  Bar.prototype.setOn = function (on) {
    if (on === this.on) return;
    this.on = on;
    this.bar.classList.toggle('is-on', on);
    if (!on) this.close();
  };

  Bar.prototype.shouldShow = function () {
    if (this.revealMode === 'always') return true;
    var top = this.sentinel ? this.sentinel.getBoundingClientRect().top : 0;
    if (top > this.topFor()) return false;
    if (this.hideAfterLast && this.spots.length) {
      var last = this.spots[this.spots.length - 1].el;
      if (last.getBoundingClientRect().bottom < this.topFor() + this.barHeight()) return false;
    }
    return true;
  };

  Bar.prototype.update = function () {
    this.bar.style.top = this.topFor() + 'px';
    this.setOn(this.shouldShow());
    this.pushHeader(this.on);
    if (this.reserve) this.root.style.minHeight = this.barHeight() + 'px';
    this.spy();
  };

  Bar.prototype.spy = function () {
    if (!this.spots.length) return;
    var line = this.topFor() + this.barHeight() + SPY_PAD;
    var current = null;
    for (var i = 0; i < this.spots.length; i++) {
      if (this.spots[i].el.getBoundingClientRect().top - line <= 0) current = this.spots[i];
      else break;
    }
    this.spots.forEach(function (spot) {
      var active = spot === current;
      spot.links.forEach(function (a) {
        a.classList.toggle('is-active', active);
        if (active) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
    });
    this.keepInView(current);
  };

  /* horizontal swipe row: nudge the active link into view */
  Bar.prototype.keepInView = function (spot) {
    if (!spot) return;
    var row = this.root.querySelector('[data-pnav-links]');
    if (!row || row.scrollWidth <= row.clientWidth) return;
    var a = null;
    spot.links.forEach(function (l) { if (row.contains(l)) a = l; });
    if (!a) return;
    var lr = a.getBoundingClientRect();
    var rr = row.getBoundingClientRect();
    if (lr.left < rr.left + 8) row.scrollLeft += lr.left - rr.left - 16;
    else if (lr.right > rr.right - 8) row.scrollLeft += lr.right - rr.right + 16;
  };

  Bar.prototype.scrollTo = function (el) {
    var y = window.pageYOffset + el.getBoundingClientRect().top - (this.topFor() + this.barHeight() + 16);
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  };

  Bar.prototype.bindLinks = function () {
    var self = this;
    this.spots.forEach(function (spot) {
      spot.links.forEach(function (a) {
        a.addEventListener('click', function (e) {
          e.preventDefault();
          self.close();
          self.scrollTo(spot.el);
        });
      });
    });
  };

  Bar.prototype.bindToggle = function () {
    var self = this;
    this.toggle = this.root.querySelector('[data-pnav-toggle]');
    if (!this.toggle) return;
    this.toggle.addEventListener('click', function () {
      if (self.root.classList.contains('is-open')) self.close();
      else self.open();
    });
    document.addEventListener('click', function (e) {
      if (!self.root.classList.contains('is-open')) return;
      if (self.bar.contains(e.target)) return;
      self.close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') self.close();
    });
  };

  /* Mobile menu button. Forwards to the theme's own hamburger rather than
     reimplementing the drawer, so submenu levels, focus handling and
     close-on-backdrop keep working. Releases the header push and hides our
     bar first. A MutationObserver on the drawer's class puts both back when
     it closes, whichever way it was closed. */
  Bar.prototype.bindMenu = function () {
    var btn = this.root.querySelector('[data-pnav-menu]');
    if (!btn) return;

    var opener = firstMatch(MENU_OPENERS);
    if (!opener) {
      if (btn.parentNode) btn.parentNode.removeChild(btn);
      return;
    }

    var self = this;
    var drawer = document.querySelector(MENU_DRAWER);

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      self.close();
      /* Release the header BEFORE the drawer opens, so the drawer is never
         laid out inside a transformed, pointer-events: none subtree. */
      self.setMenuOpen(true);
      opener.click();
      /* If the click did not actually open anything, take the bar back
         rather than leaving the shopper on a blank screen. */
      if (drawer) {
        setTimeout(function () {
          if (!drawer.classList.contains(MENU_OPEN_CLASS)) self.setMenuOpen(false);
        }, 400);
      }
    });

    if (!drawer) return;

    new MutationObserver(function () {
      var isOpen = drawer.classList.contains(MENU_OPEN_CLASS);
      if (isOpen !== self.menuOpen) self.setMenuOpen(isOpen);
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  };

  Bar.prototype.setMenuOpen = function (open) {
    this.menuOpen = open;
    this.bar.classList.toggle('is-menu-open', open);
    if (open) this.pushHeader(false);
    else this.update();
  };

  Bar.prototype.open = function () {
    this.root.classList.add('is-open');
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
  };

  Bar.prototype.close = function () {
    if (!this.root.classList.contains('is-open')) return;
    this.root.classList.remove('is-open');
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
  };

  /* ==========================================================
     VARIANT PICKER
     ========================================================== */

  function Picker(root, bar) {
    this.root = root;
    this.bar = bar;
    this.el = root.querySelector('[data-pnav-picker]');
    if (!this.el) return;

    var json = root.querySelector('[data-pnav-variants]');
    try {
      this.variants = JSON.parse(json.textContent);
    } catch (e) {
      this.variants = [];
    }
    if (!this.variants.length) return;

    this.panel = this.el.querySelector('[data-pnav-panelbox]');
    this.groups = Array.prototype.slice.call(this.el.querySelectorAll('[data-pnav-optgroup]'));
    this.idField = this.el.querySelector('[data-pnav-id]');
    this.qtyField = this.el.querySelector('[data-pnav-qtyfield]');
    this.qtyInput = this.el.querySelector('[data-pnav-qtyinput]');
    this.priceEl = this.el.querySelector('[data-pnav-price]');
    this.compareEl = this.el.querySelector('[data-pnav-compare]');
    this.mediaEl = this.el.querySelector('[data-pnav-media]');
    this.submit = this.el.querySelector('[data-pnav-submit]');
    this.ctaText = this.el.querySelector('[data-pnav-cta-text]');
    this.ctaLabel = this.ctaText ? this.ctaText.textContent.trim() : 'Add to cart';
    this.lastFocus = null;

    this.bindOpeners();
    this.bindOptions();
    this.bindQty();
    this.bindClose();
    this.bindSubmit();
    this.sync();
  }

  Picker.prototype.bindOpeners = function () {
    var self = this;
    Array.prototype.slice.call(this.root.querySelectorAll('[data-pnav-open]')).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        self.open(btn.getAttribute('data-pnav-open'), btn);
      });
    });
  };

  Picker.prototype.bindOptions = function () {
    var self = this;
    this.el.addEventListener('change', function (e) {
      if (e.target && e.target.hasAttribute('data-pnav-value')) self.sync();
    });
  };

  Picker.prototype.bindQty = function () {
    var self = this;
    if (!this.qtyInput) return;
    Array.prototype.slice.call(this.el.querySelectorAll('[data-pnav-qty]')).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = parseInt(btn.getAttribute('data-pnav-qty'), 10) || 1;
        var next = (parseInt(self.qtyInput.value, 10) || 1) + step;
        self.qtyInput.value = Math.max(1, next);
        self.syncQty();
      });
    });
    this.qtyInput.addEventListener('input', function () { self.syncQty(); });
  };

  Picker.prototype.syncQty = function () {
    if (!this.qtyField) return;
    var v = Math.max(1, parseInt(this.qtyInput ? this.qtyInput.value : 1, 10) || 1);
    this.qtyField.value = v;
  };

  Picker.prototype.bindClose = function () {
    var self = this;
    Array.prototype.slice.call(this.el.querySelectorAll('[data-pnav-close]')).forEach(function (el) {
      el.addEventListener('click', function () { self.close(); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.el.classList.contains('is-open')) self.close();
    });
  };

  /* Theme's <product-form> adds .loading while the fetch is in flight and
     removes it when the cart responds. Close on the removal, with a timeout
     in case the custom element is not on the page and the form posts natively. */
  Picker.prototype.bindSubmit = function () {
    var self = this;
    if (!this.submit) return;
    this.submit.addEventListener('click', function () {
      var closed = false;
      var t;
      var done = function () {
        if (closed) return;
        closed = true;
        obs.disconnect();
        clearTimeout(t);
        self.close();
      };
      var obs = new MutationObserver(function () {
        if (self.submit.classList.contains('loading')) {
          self.submit.dataset.pnavSeenLoading = '1';
        } else if (self.submit.dataset.pnavSeenLoading === '1') {
          done();
        }
      });
      obs.observe(self.submit, { attributes: true, attributeFilter: ['class'] });
      t = setTimeout(done, 2500);
    });
  };

  Picker.prototype.selection = function () {
    return this.groups.map(function (g) {
      var checked = g.querySelector('[data-pnav-value]:checked');
      return checked ? checked.value : null;
    });
  };

  Picker.prototype.match = function (sel) {
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      var ok = true;
      for (var j = 0; j < sel.length; j++) {
        if (sel[j] !== null && v.options[j] !== sel[j]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  /* Grey out values that cannot be reached from the rest of the selection. */
  Picker.prototype.markUnavailable = function (sel) {
    var self = this;
    this.groups.forEach(function (g, gi) {
      Array.prototype.slice.call(g.querySelectorAll('[data-pnav-value]')).forEach(function (input) {
        var probe = sel.slice();
        probe[gi] = input.value;
        var hit = null;
        for (var i = 0; i < self.variants.length; i++) {
          var v = self.variants[i];
          var ok = true;
          for (var j = 0; j < probe.length; j++) {
            if (probe[j] !== null && v.options[j] !== probe[j]) { ok = false; break; }
          }
          if (ok && v.available) { hit = v; break; }
        }
        var chip = g.querySelector('label[for="' + input.id + '"]');
        if (chip) chip.classList.toggle('is-unavailable', !hit);
      });
      var chosen = g.querySelector('[data-pnav-chosen]');
      var current = g.querySelector('[data-pnav-value]:checked');
      if (chosen && current) chosen.textContent = current.value;
    });
  };

  Picker.prototype.sync = function () {
    var sel = this.selection();
    var v = this.match(sel);
    this.markUnavailable(sel);
    this.syncQty();

    if (this.idField && v) this.idField.value = v.id;

    if (this.priceEl && v) this.priceEl.textContent = v.price;
    if (this.compareEl) {
      if (v && v.hasCompare) {
        this.compareEl.textContent = v.compare;
        this.compareEl.hidden = false;
      } else {
        this.compareEl.hidden = true;
      }
    }

    if (this.mediaEl && v && v.image) {
      var img = this.mediaEl.querySelector('img');
      if (img) {
        img.removeAttribute('srcset');
        img.src = v.image;
      }
    }

    if (this.submit) {
      var ok = !!(v && v.available);
      this.submit.disabled = !ok;
      if (this.ctaText) {
        this.ctaText.textContent = v ? (v.available ? this.ctaLabel : 'Sold out') : 'Unavailable';
      }
    }

    var wallet = this.el.querySelector('[data-pnav-walletwrap]');
    if (wallet) wallet.style.display = v && v.available ? '' : 'none';
  };

  Picker.prototype.place = function (anchor) {
    if (this.el.getAttribute('data-mode') !== 'dropdown' || !this.panel) return;
    if (window.matchMedia('(max-width: 767px)').matches) {
      this.panel.style.top = '';
      this.panel.style.left = '';
      this.panel.style.right = '';
      this.panel.style.width = '';
      return;
    }
    var barRect = this.bar && this.bar.bar ? this.bar.bar.getBoundingClientRect() : { bottom: 56 };
    var a = anchor ? anchor.getBoundingClientRect() : null;
    var width = Math.min(400, window.innerWidth - 32);
    var right = a ? Math.max(16, window.innerWidth - a.right) : 16;
    this.panel.style.top = barRect.bottom + 10 + 'px';
    this.panel.style.right = right + 'px';
    this.panel.style.left = 'auto';
    this.panel.style.width = width + 'px';
  };

  Picker.prototype.open = function (intent, anchor) {
    this.lastFocus = document.activeElement;
    this.el.setAttribute('data-intent', intent || 'cart');
    this.el.setAttribute('aria-hidden', 'false');
    this.place(anchor);
    this.el.classList.add('is-open');
    document.documentElement.classList.add('hp-pnav-locked');
    document.body.style.overflow = 'hidden';
    var first = this.el.querySelector('[data-pnav-value]:checked') || this.submit;
    if (first && first.focus) setTimeout(function () { first.focus({ preventScroll: true }); }, 60);
  };

  Picker.prototype.close = function () {
    if (!this.el.classList.contains('is-open')) return;
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('hp-pnav-locked');
    document.body.style.overflow = '';
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
  };

  /* ==========================================================
     INIT
     ========================================================== */

  function init() {
    Array.prototype.slice.call(document.querySelectorAll('[data-pnav-root]')).forEach(function (root) {
      if (root.__pnav) return;
      root.__pnav = true;
      var bar = new Bar(root);
      root.__pnavBar = bar;
      root.__pnavPicker = new Picker(root, bar);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-pnav-root]') : null;
    if (root) root.__pnav = false;
    init();
  });

  /* In the theme editor, selecting the section should show it even at the
     top of the page, otherwise the merchant is editing something invisible. */
  document.addEventListener('shopify:section:select', function (e) {
    var root = e.target.querySelector('[data-pnav-root]');
    if (root && root.__pnavBar) {
      root.__pnavBar.revealMode = 'always';
      root.__pnavBar.update();
    }
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    var root = e.target.querySelector('[data-pnav-root]');
    if (root && root.__pnavBar) {
      root.__pnavBar.revealMode = root.dataset.reveal || 'scroll';
      root.__pnavBar.update();
    }
  });
})();
